import React from "react";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";
import { Label } from "@/dentlab/src/components/ui/label";
import { GuarantorSelect } from "../../../../forms/form-input-components/selects/guarantor.select";
import { BankAccountSelect } from "../../../../forms/form-components/bank-account/input-components/bank-account.select";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { TpValue } from "@/dentlab/src/types/enums";
import { REGION_METADATA } from "@/dentlab/src/types/region.types";
import { CustomTextFieldWithPercentage } from "../../../../forms/form-input-components/custom-input-components/custom-text-field-with-percentage.component";
import { PriceListSelect } from "../../../../forms/form-components/price-list-select.component";
import { CatalogSelect } from "../../../../forms/form-components/catalog-select.component";
import { CustomSelectShadcn } from "@/dentlab/src/components/custom-select-shadcn.component";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/dentlab/src/components/ui/card";
import { CustomToggleButtonGroup } from "@/dentlab/src/components/custom-toggle-button/custom-toggle-button-group.component";
import { useCatalogsStore } from "@/dentlab/src/store/Catalogs";

export const RechnungenPage: React.FC = () => {
	const {
		client,
		activatedRegions,
		clientFormTextFields,
		updateClientFormTextField,
		handleImmediateClientFieldUpdate,
		setClientDefaultCatalog,
	} = useCentralStore((state) => ({
		client: state.client,
		activatedRegions: state.activatedRegions,
		clientFormTextFields: state.clientFormTextFields,
		updateClientFormTextField: state.updateClientFormTextField,
		handleImmediateClientFieldUpdate:
			state.handleImmediateClientFieldUpdate,
		setClientDefaultCatalog: state.setClientDefaultCatalog,
	}));

	const catalogs = useCatalogsStore((state) => state.catalogs);

	const defaultClientCatalogId =
		(client as any)?.default_catalogs_default_id ||
		(client as any)?.default_catalogs_custom_id;

	const regionOptions = [
		{ value: "", label: "Labor-Standard" },
		...activatedRegions
			.filter((region) => region.id)
			.map((region) => ({
				value: region.id!.toString(),
				label: REGION_METADATA[region.region].label,
			})),
	];

	if (!client) {
		return (
			<div className="p-6">
				<div className="text-gray-500">Kein Kunde ausgewählt</div>
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="max-w-6xl">
				<h1 className="text-2xl font-semibold text-gray-900 mb-6">
					Rechnungen und Dokumente
				</h1>
				<div className="space-y-6">
					{/* Rabatte Section */}
					<Card>
						<CardHeader>
							<CardTitle>Rabatte</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<CustomTextFieldWithPercentage
									label="Rabattsatz Arbeit"
									name="discount_work"
									value={
										clientFormTextFields.discount_work || ""
									}
									onChange={(e) =>
										updateClientFormTextField(
											"discount_work",
											e.target.value
										)
									}
									fullWidth
								/>
								<CustomTextFieldWithPercentage
									label="Rabattsatz Material"
									name="discount_material"
									value={
										clientFormTextFields.discount_material ||
										""
									}
									onChange={(e) =>
										updateClientFormTextField(
											"discount_material",
											e.target.value
										)
									}
									fullWidth
								/>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<CustomTextFieldWithPercentage
									label="Monatsrabatt"
									name="monthly_discount"
									value={
										clientFormTextFields.monthly_discount ||
										""
									}
									onChange={(e) =>
										updateClientFormTextField(
											"monthly_discount",
											e.target.value
										)
									}
									fullWidth
								/>
								<div className="flex items-center space-x-2 mt-6">
									<Checkbox
										id="is_monthly_discount_only_work"
										checked={
											client.is_monthly_discount_only_work
												? true
												: false
										}
										onCheckedChange={(checked) =>
											handleImmediateClientFieldUpdate(
												"is_monthly_discount_only_work",
												checked as boolean
											)
										}
									/>
									<Label
										htmlFor="is_monthly_discount_only_work"
										className="text-sm font-normal cursor-pointer"
									>
										Monatsrabatt nur auf Arbeit
									</Label>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Konto & Garant Section */}
					<Card>
						<CardHeader>
							<CardTitle>Konto & Garant</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<GuarantorSelect
									onChange={(value) => {
										handleImmediateClientFieldUpdate(
											"guarantor_id",
											value || null
										);
									}}
									value={client.guarantor_id}
								/>
								<BankAccountSelect
									label="Konto"
									value={
										client.bank_account_id?.toString() ??
										null
									}
									onChange={(value) =>
										handleImmediateClientFieldUpdate(
											"bank_account_id",
											value
										)
									}
									helperText="Wenn kein Konto gewählt ist, wird das Standardkonto Ihres Labors benutzt."
								/>
							</div>
						</CardContent>
					</Card>

					{/* Tarif-Einstellungen Section */}
					<Card>
						<CardHeader>
							<CardTitle>Tarif-Einstellungen</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div>
								<div className="text-sm font-medium mb-2">
									Standard Tarifwert
								</div>
								<CustomToggleButtonGroup
									value={client.default_tp_value}
									onChange={(value) => {
										handleImmediateClientFieldUpdate(
											"default_tp_value",
											value
										);
									}}
									options={[
										{ label: "5.55", value: TpValue.OLD },
										{ label: "1.0", value: TpValue.NEW },
										{
											label: "Preislisten-Standard",
											value: null,
										},
									]}
									isInput
									ariaLabel="Standard Tarifwert"
									testId="client-default-tp-value"
								/>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<CustomTextFieldWithPercentage
									label="TP-Variation (+/-)"
									name="default_tp_variation"
									value={
										clientFormTextFields.default_tp_variation ||
										""
									}
									onChange={(e) =>
										updateClientFormTextField(
											"default_tp_variation",
											e.target.value
										)
									}
									fullWidth
									classNameInput="h-12"
								/>
								<div>
									<PriceListSelect
										value={
											(client as any)
												?.default_price_list_id || ""
										}
										onChange={(value) => {
											handleImmediateClientFieldUpdate(
												"default_price_list_id",
												value
											);
										}}
										emptyLabel="Labor-Standard"
									/>
								</div>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<CatalogSelect
										value={defaultClientCatalogId || ""}
										onChange={(value) => {
											if (!value) {
												setClientDefaultCatalog(
													null,
													"default"
												);
											} else {
												const catalog = catalogs.find(
													(c) => c.id === value
												);
												if (catalog) {
													setClientDefaultCatalog(
														value,
														catalog.catalog_type as
															| "default"
															| "custom"
													);
												}
											}
										}}
										emptyLabel="Labor-Standard"
									/>
								</div>
								{activatedRegions.length > 1 && (
									<div>
										<CustomSelectShadcn
											options={regionOptions}
											inputLabel="Region"
											value={
												client.default_activated_region_id?.toString() ||
												""
											}
											onChange={(value) => {
												handleImmediateClientFieldUpdate(
													"default_activated_region_id",
													value ? Number(value) : null
												);
											}}
										/>
									</div>
								)}
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
};
